import { createWebHistory, createRouter, RouteRecordRaw, type RouteLocationNormalized, type RouteLocationRaw } from 'vue-router';
import i18n from '@/lang/index';
const { t } = i18n.global;
/* Layout */
import Layout from '@/layout/index.vue';
import HeaderLayout from '@/layout/HeaderLayout.vue';

/**
 * 将 `/login/{租户ID}` 兼容入口转换为登录页现有的 redirect 查询参数格式。
 * @param to 当前匹配到的动态租户登录路由
 * @returns `/login?redirect=/login/{租户ID}` 对应的路由位置
 * @remarks 该方法只生成重定向目标且无副作用；路由本身已约束 tenantId 必须为纯数字
 */
const redirectTenantLogin = (to: RouteLocationNormalized): RouteLocationRaw => {
  const tenantId = String(to.params.tenantId);
  return {
    path: '/login',
    query: { redirect: `/login/${tenantId}` }
  };
};

/**
 * Note: 路由配置项
 *
 * hidden: true                     // 当设置 true 的时候该路由不会再侧边栏出现 如401，login等页面，或者如一些编辑页面/edit/1
 * alwaysShow: true                 // 当你一个路由下面的 children 声明的路由大于1个时，自动会变成嵌套的模式--如组件页面
 *                                  // 只有一个时，会将那个子路由当做根路由显示在侧边栏--如引导页面
 *                                  // 若你想不管路由下面的 children 声明的个数都显示你的根路由
 *                                  // 你可以设置 alwaysShow: true，这样它就会忽略之前定义的规则，一直显示根路由
 * redirect: noRedirect             // 当设置 noRedirect 的时候该路由在面包屑导航中不可被点击
 * name:'router-name'               // 设定路由的名字，一定要填写不然使用<keep-alive>时会出现各种问题
 * query: '{"id": 1, "name": "ry"}' // 访问路由的默认传递参数
 * roles: ['admin', 'common']       // 访问路由的角色权限
 * permissions: ['a:a:a', 'b:b:b']  // 访问路由的菜单权限
 * meta : {
    noCache: true                   // 如果设置为true，则不会被 <keep-alive> 缓存(默认 false)
    title: 'title'                  // 设置该路由在侧边栏和面包屑中展示的名字
    icon: 'svg-name'                // 设置该路由的图标，对应路径src/assets/icons/svg
    breadcrumb: false               // 如果设置为false，则不会在breadcrumb面包屑中显示
    activeMenu: '/system/user'      // 当路由设置了该属性，则会高亮相对应的侧边栏。
  }
 */

// 公共路由
export const constantRoutes: RouteRecordRaw[] = [
  {
    path: '/redirect',
    component: Layout,
    hidden: true,
    children: [
      {
        path: '/redirect/:path(.*)',
        component: () => import('@/views/redirect/index.vue')
      }
    ]
  },
  {
    path: '/social-callback',
    hidden: true,
    component: () => import('@/layout/components/SocialCallback/index.vue')
  },
  {
    // 兼容直接输入 `/login/886467`，统一转换为登录页 onMounted 已支持的 redirect 形式。
    path: '/login/:tenantId(\\d+)',
    redirect: redirectTenantLogin,
    hidden: true
  },
  {
    path: '/login',
    component: () => import('@/views/login.vue'),
    hidden: true
  },
  {
    path: '/register',
    component: () => import('@/views/register.vue'),
    hidden: true
  },
  // 直播分享 H5 观看页（扫码免登录）
  {
    path: '/sharedVideo',
    component: () => import('@/views/missionHub/liveShareH5/index.vue'),
    name: 'sharedVideo',
    meta: { title: t('platformUtils.router.liveShareTitle') },
    hidden: true
  },
  {
    path: '/:pathMatch(.*)*',
    component: () => import('@/views/error/404.vue'),
    hidden: true
  },
  {
    path: '/401',
    component: () => import('@/views/error/401.vue'),
    hidden: true
  },
  {
    path: '/',
    component: Layout,
    redirect: '/OverallSituation/main',
    children: [
      {
        path: '/home',
        component: () => import('@/views/index.vue'),
        name: 'home',
        meta: { title: t('navbar.dashboard'), icon: 'dashboard', affix: true }
      }
    ]
  },
  // 单机任务详情
  {
    path: '/detail',
    component: () => import('@/views/missionHub/standAlone/index.vue'),
    name: 'detail',
    meta: { title: t('role.details'), icon: '', affix: true }
  },
  //任务数据详情
  {
    path: '/taskDataDetail',
    component: () => import('@/views/missionHub/TaskData/TaskDetail.vue'),
    name: 'taskDataDetail',
    meta: { title: t('platformUtils.router.taskDataDetailTitle'), icon: '', affix: true }
  },
  // 地面任务追溯使用统一任务 ID，独立于飞行执行记录详情。
  {
    path: '/groundTaskDataDetail',
    component: () => import('@/views/missionHub/TaskData/GroundTaskDetail/index.vue'),
    name: 'groundTaskDataDetail',
    meta: { title: t('mission.dispatchUnified.groundDetail'), icon: '', affix: true }
  },
  // 作业场景协同页面：使用独立全屏布局承载地图、参与设备和实时画面。
  {
    path: '/operation-scenario',
    component: () => import('@/views/manageCenter/operationScenario/index.vue'),
    name: 'operationScenarioWorkspace',
    hidden: true,
    meta: { title: t('operationScenario.workspaceTitle'), icon: '' }
  },
  // 四足机器人工作页：使用独立全屏布局承载运动控制和负载控制界面。
  {
    path: '/operation-scenario/robot',
    component: () => import('@/views/manageCenter/operationScenario/robot.vue'),
    name: 'operationScenarioRobotWorkspace',
    hidden: true,
    meta: { title: t('operationScenario.robotWorkspaceTitle'), icon: '' }
  },
  // 集群任务跟踪
  {
    path: '/clusterDetail',
    component: () => import('@/views/missionHub/QuestMange/index.vue'),
    name: 'clusterDetail',
    meta: { title: t('mission.clusterTaskTracking'), icon: '', affix: true }
  },
  {
    path: '/cloudAtlas',
    component: () => import('@/views/safetyControl/CloudAtlas/index.vue'),
    name: 'cloudAtlas',
    meta: { title: t('platformUtils.router.cloudAtlasTitle'), icon: '', affix: true }
  },
  {
    path: '/user',
    component: Layout,
    hidden: true,
    redirect: 'noredirect',
    children: [
      {
        path: 'profile',
        component: () => import('@/views/system/user/profile/index.vue'),
        name: 'Profile',
        meta: { title: t('navbar.personalCenter'), icon: 'user' }
      }
    ]
  },
  {
    path: '/workflowee',
    name: 'WarmFlowDesign',
    component: () => import('@/views/workflow/processDefinition/design.vue'),
    hidden: true,
    meta: { title: t('menuList.flowDesign') }
  },
  {
    path: '/E6AairLineManager',
    name: 'E6AairLineManager',
    component: () => import('@/views/RoutePlanning/E6AairLineManager/index.vue')
  },
  {
    path: '/flightPathManager',
    name: 'FlightPathManager',
    component: () => import('@/views/RoutePlanning/flightPathManager/index.vue')
  },
  {
    path: '/zyFlightManager',
    name: 'zyFlightManager',
    component: () => import('@/views/RoutePlanning/ZiyanPathManager/index.vue')
  },
  {
    path: '/polygonalRoute',
    name: 'polygonalRoute',
    component: () => import('@/views/RoutePlanning/polygonalRoute/index.vue')
  },
  {
    path: '/sideElevation',
    name: 'sideElevation',
    component: () => import('@/views/RoutePlanning/sideElevation/index.vue')
  },
  {
    path: '/pointCloud',
    name: 'pointCloud',
    component: () => import('@/views/RoutePlanning/pointCloud/index.vue')
  },
  {
    path: '/videoPreview',
    name: 'videoPreview',
    component: () => import('@/views/videoPreview/index.vue')
  },
  {
    path: '/SynthesizeDTS',
    name: 'SynthesizeDTS',
    component: () => import('@/views/shuziluansheng/SynthesizeDTS/index.vue')
  },
  {
    path: '/equipmentSituation',
    name: 'EquipmentSituation',
    component: () => import('@/views/manageCenter/EquipmentSituation/index.vue')
  },
  {
    path: '/taskStatistics',
    name: 'TaskStatistics',
    component: () => import('@/views/manageCenter/taskStatistics/CesiumBox/index.vue')
  },
  {
    path: '/dangerStatistics',
    name: 'DangerStatistics',
    component: () => import('@/views/manageCenter/dangerStatistics/index.vue')
  },
  {
    path: '/dispatch',
    name: 'dispatch',
    component: () => import('@/views/missionHub/dispatch/index.vue')
  },
  {
    path: '/shuziluansheng',
    component: HeaderLayout,
    hidden: true,
    permissions: [],
    children: [
      {
        path: '/shuziluansheng/WorldMapDTS',
        name: 'WorldMapDTS',
        component: () => import('@/views/shuziluansheng/WorldMapDTS/index.vue')
      }
    ]
  },
  // 城市治理 — 大屏（独立全屏页面，侧边栏不显式挂载时可由此路径进入开发联调）
  // {
  //   path: '/governance/big-screen',
  //   name: 'GovernanceBigScreen',
  //   component: () => import('@/views/governance/bigScreen/index.vue'),
  //   meta: { title: '城市治理大屏', icon: '', affix: false }
  // }
];

// 动态路由，基于用户权限动态去加载
export const dynamicRoutes: RouteRecordRaw[] = [
  {
    path: '/system/user-auth',
    component: Layout,
    hidden: true,
    permissions: ['system:user:edit'],
    children: [
      {
        path: 'role/:userId(\\d+)',
        component: () => import('@/views/system/user/authRole.vue'),
        name: 'AuthRole',
        meta: { title: t('system.user.assignRoles'), activeMenu: '/system/user', icon: '' }
      }
    ]
  },
  {
    path: '/system/role-auth',
    component: Layout,
    hidden: true,
    permissions: ['system:role:edit'],
    children: [
      {
        path: 'user/:roleId(\\d+)',
        component: () => import('@/views/system/role/authUser.vue'),
        name: 'AuthUser',
        meta: { title: t('system.role.assignUsers'), activeMenu: '/system/role', icon: '' }
      }
    ]
  },
  {
    path: '/system/dict-data',
    component: Layout,
    hidden: true,
    permissions: ['system:dict:list'],
    children: [
      {
        path: 'index/:dictId(\\d+)',
        component: () => import('@/views/system/dict/data.vue'),
        name: 'Data',
        meta: { title: t('dict.dictData'), activeMenu: '/system/dict', icon: '' }
      }
    ]
  },
  {
    path: '/system/oss-config',
    component: Layout,
    hidden: true,
    permissions: ['system:ossConfig:list'],
    children: [
      {
        path: 'index',
        component: () => import('@/views/system/oss/config.vue'),
        name: 'OssConfig',
        meta: { title: t('system.peizhi'), activeMenu: '/system/oss', icon: '' }
      }
    ]
  },
  // {
  //   path: '/tool/gen-edit',
  //   component: Layout,
  //   hidden: true,
  //   permissions: ['tool:gen:edit'],
  //   children: [
  //     {
  //       path: 'index/:tableId(\\d+)',
  //       component: () => import('@/views/tool/gen/editTable.vue'),
  //       name: 'GenEdit',
  //       meta: { title: t('system.xgscpz'), activeMenu: '/tool/gen', icon: '', noCache: true }
  //     }
  //   ]
  // },
  {
    path: '/OrderInfo/workOrder',
    component: Layout,
    hidden: true,
    permissions: ['apg:workOrder:details'],
    children: [
      {
        path: 'workOrderDetails/:orderId(\\d+)',
        component: () => import('@/views/jobCenter/OrderInfo/workOrder/workOrderDetails.vue'),
        name: 'WorkOrderDetails',
        meta: { title: t('system.workOrderDetail'), icon: '' }
      }
    ]
  }
];
/**
 * 创建路由
 */
const router = createRouter({
  history: createWebHistory(import.meta.env.VITE_APP_CONTEXT_PATH),
  routes: constantRoutes,
  // 刷新时，滚动条位置还原
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0 };
    }
  }
});

export default router;
